import {
  type ReactElement,
  cloneElement,
  useCallback,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { ThumbsUpIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import type { IdeaWithVotes } from "simplydo/interfaces/types/ideaTypes";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import actions from "actions";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

type IdeaLikeHandlerProps = {
  idea: IdeaWithVotes;
  trigger: ReactElement<{ onClick?: () => void }>;
  onLikeIdea: (ideaId: string, addingLike: boolean) => void;
  challenge: OpenAPI.Schemas["Challenge"];
};

const IdeaLikeHandler = ({
  idea,
  onLikeIdea,
  challenge,
  trigger,
}: IdeaLikeHandlerProps) => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const ideaId = idea._id;
  const hasVotedForIdea = idea.votedFor;

  const { t } = useTranslation();

  const hasSeenIdeaLikeInformationModalThisSession = useAppSelector(
    (state) => state.organisations.hasSeenIdeaLikeInformationModalThisSession,
  );
  const [modalOpen, setModalOpen] = useState(false);

  const shouldShowLikeNotificationModal = useMemo(
    () =>
      !util.ideaMeetsChallengeVisibilityLimits(idea, challenge) &&
      (util.canManageChallenge(user, challenge) ||
        util.hasPermission(user, "challenge.viewIdeas", challenge?._id)),
    [challenge, user, idea],
  );

  const onClickLikeButton = useCallback(() => {
    if (
      shouldShowLikeNotificationModal &&
      !hasSeenIdeaLikeInformationModalThisSession &&
      !hasVotedForIdea
    ) {
      setModalOpen(true);
    } else {
      onLikeIdea(ideaId, !hasVotedForIdea);
    }
  }, [
    shouldShowLikeNotificationModal,
    hasSeenIdeaLikeInformationModalThisSession,
    onLikeIdea,
    ideaId,
    hasVotedForIdea,
  ]);

  const onClickModalLikeButton = useCallback(() => {
    dispatch(
      actions.organisations.setHasSeenIdeaLikeInformationModalThisSession(true),
    );
    onLikeIdea(ideaId, !hasVotedForIdea);
    setModalOpen(false);
  }, [dispatch, onLikeIdea, ideaId, hasVotedForIdea]);

  const onCloseModal = useCallback(() => {
    dispatch(
      actions.organisations.setHasSeenIdeaLikeInformationModalThisSession(true),
    );
    setModalOpen(false);
  }, [dispatch]);

  return (
    <>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalHeader>
          {t("common:capitalise", { key: "generic.idea" })} Like Information
        </ModalHeader>
        <ModalContent>
          When you &quot;like&quot; {t("generic.ideaWithArticle")} in this
          &quot;
          {t("generic.challenge")}, the author(s) of the {t("generic.idea")}
          will be notified via email and/or push notification. Your name will be
          visible to the author(s) of the {t("generic.idea")}, and they will be
          able to see that you liked their {t("generic.idea")}. You are seeing
          this message because you have special permission to view{" "}
          {t("generic.ideas")} in this {t("generic.challenge")}, and this{" "}
          {t("generic.challenge")} only allows{" "}
          {challenge.ideaVisibilityLimits?.length
            ? `users to see ${t("generic.ideas")} that are ${challenge.ideaVisibilityLimits.join("/")}`
            : `people with special permissions to see ${t("generic.ideas")}`}
          .
        </ModalContent>
        <ModalActions>
          <Button
            onClick={onCloseModal}
          >{`Cancel and don't like ${t("generic.idea")}`}</Button>
          <Button onClick={onClickModalLikeButton} color="primary">
            <Icon icon={ThumbsUpIcon} />
            {`I understand and still want to like this ${t("generic.idea")}`}
          </Button>
        </ModalActions>
      </Modal>

      {cloneElement(trigger, { onClick: onClickLikeButton })}
    </>
  );
};

export default IdeaLikeHandler;
